import { apiRequestEnvelope } from '@scc/lib'
import type { ShopRepository } from '@/data/repositories/types'
import { toApiDate } from '@/lib/api-date'
import type { DataFreshness, DateRange } from '@/modules/shared/types'
import type { ProductPerformance, ShopDailyMetric, ShopSettlementDaily } from '@/modules/shop/types'

/**
 * Freshness reported per (store, range) fetch. Keyed rather than kept as a
 * single "last" value so concurrent stores/ranges can't read each other's
 * metadata. The ShopRepository contract stays unchanged.
 */
const freshnessByQuery = new Map<string, DataFreshness>()

export function freshnessKey(clientId: string, range: DateRange): string {
  return `${clientId}:${toApiDate(range.from)}:${toApiDate(range.to)}`
}

export function getShopFreshness(clientId: string, range: DateRange): DataFreshness | null {
  return freshnessByQuery.get(freshnessKey(clientId, range)) ?? null
}

export function createApiShopRepository(): ShopRepository {
  return {
    async getDailyMetrics(clientId: string, range: DateRange): Promise<ShopDailyMetric[]> {
      const start = toApiDate(range.from)
      const end = toApiDate(range.to)
      const { data, meta } = await apiRequestEnvelope<
        ShopDailyMetric[],
        { freshness?: DataFreshness } | undefined
      >(`/analytics/shop/daily?start=${start}&end=${end}`, {
        headers: { 'X-Store-Id': clientId },
      })
      if (meta?.freshness) {
        freshnessByQuery.set(freshnessKey(clientId, range), meta.freshness)
      }
      return data ?? []
    },
    async getSettlementDaily(clientId: string, range: DateRange): Promise<ShopSettlementDaily[]> {
      const start = toApiDate(range.from)
      const end = toApiDate(range.to)
      const { data } = await apiRequestEnvelope<ShopSettlementDaily[], undefined>(
        `/analytics/shop/settlements/daily?start=${start}&end=${end}`,
        { headers: { 'X-Store-Id': clientId } },
      )
      return data ?? []
    },
    async getProductPerformance(clientId: string, range: DateRange): Promise<ProductPerformance[]> {
      const start = toApiDate(range.from)
      const end = toApiDate(range.to)
      // Served by the products module (the old /analytics/shop/products
      // endpoint never existed server-side — this query 404'd silently
      // until the Product Performance page shipped its real backend).
      const { data } = await apiRequestEnvelope<
        { products: Array<{ productId: string; productName: string; gmv: number; orders: number; unitsSold: number; impressions: number; pageViews: number; ctr: number }> },
        undefined
      >(`/analytics/products/performance?start=${start}&end=${end}`, {
        headers: { 'X-Store-Id': clientId },
      })
      return (data?.products ?? []).map((p) => ({
        productId: p.productId,
        productName: p.productName,
        sku: '',
        gmv: p.gmv,
        orders: p.orders,
        units: p.unitsSold,
        impressions: p.impressions,
        clicks: p.pageViews,
        ctr: p.ctr,
      }))
    },
  }
}

export const shopRepository = createApiShopRepository()
